// Presentation logic for the pre-plan body of the My Course tab.
//
// The screen shows three setup stages (background interview, language
// assessment, plan build) and exactly one available action. Everything visual
// on that screen keys on the values this module returns, so the panel caption,
// the focus card and the setup list can never describe three different stages.

import { CourseStage } from "@/constants/courseStages.js";

// Derived from the constants file rather than re-listed here, so adding a stage
// there cannot leave this module describing a different course.
const STAGE_ORDER = Object.values(CourseStage);

/**
 * The three stages the pre-plan body draws, in the order it draws them:
 * COLLECT_BACKGROUND, ASSESS_LEARNER, BUILD_PLAN. PLAN_IN_PROGRESS and
 * COMPLETED are past the setup phase, so everything from PLAN_IN_PROGRESS
 * onwards is excluded.
 * @type {string[]}
 */
export const SETUP_STAGES = Object.freeze(
  STAGE_ORDER.slice(0, STAGE_ORDER.indexOf(CourseStage.PLAN_IN_PROGRESS))
);

/**
 * True when a stage entry records a finish time. Entries arrive straight from
 * RTDB, which drops empty objects, so an entry can legitimately be `true`, `{}`
 * or missing altogether; only an object can carry completedAt.
 */
function isStageCompleted(entry) {
  return Boolean(entry && typeof entry === "object" && entry.completedAt);
}

/**
 * Describes the pre-plan setup phase of a course for rendering.
 *
 * A stage counts as done when its own `completedAt` is stamped, or when the
 * course pointer has already moved past it. Both rules are needed, because the
 * two values are not always written together.
 *
 * The `completedAt` rule covers the background interview.
 * `functions/source/helpers/courseSetup.py` stamps
 * `stages/COLLECT_BACKGROUND/completedAt` in a write of its own and advances
 * `currentStage` later, so a course can sit with the background interview
 * finished while the pointer still names COLLECT_BACKGROUND; without this rule
 * the caption would read "Step 1 of 3" above a Start Assessment button.
 *
 * The pointer rule covers a stage the backend has retired without this client
 * seeing the stamp, and it is also what keeps an unfinished stage below the
 * pointer from being drawn as the current one.
 * `functions/source/helpers/Lessons.py:246-249` does write
 * `stages/BUILD_PLAN/completedAt`, but in the same atomic update that moves
 * `currentStage` to PLAN_IN_PROGRESS, so for BUILD_PLAN the two rules always
 * agree.
 *
 * The function is total: a null, missing or unrecognised `currentStage` and a
 * missing `stages` map are all ordinary inputs, because the template renders
 * against metadata the backend may not have finished writing.
 *
 * @param {string|null|undefined} currentStage - `metadata.currentStage`
 * @param {object|null|undefined} stages - `metadata.stages`
 * @returns {{
 *   stepIndex: number,
 *   effectiveStage: string,
 *   rows: Array<{ stage: string, status: "done"|"current"|"upcoming" }>
 * }} `stepIndex` is the 1-based position of `effectiveStage` among the setup
 *   stages, clamped to the last one once every setup stage is done, so the
 *   caption never reads "Step 4 of 3".
 */
export function resolveCourseStagePresentation(currentStage, stages) {
  const pointerIndex = STAGE_ORDER.indexOf(currentStage);
  const stageMap = stages && typeof stages === "object" ? stages : {};

  const doneFlags = SETUP_STAGES.map(
    (stage, index) =>
      isStageCompleted(stageMap[stage]) ||
      (pointerIndex >= 0 && index < pointerIndex)
  );

  const firstUnfinished = doneFlags.indexOf(false);
  // Every setup stage done means the course has moved on to the plan (or past
  // it). The panel keeps showing the last setup stage in that case.
  const effectiveIndex =
    firstUnfinished === -1 ? SETUP_STAGES.length - 1 : firstUnfinished;
  const effectiveStage = SETUP_STAGES[effectiveIndex];

  // An unrecognised pointer means the course has not really started this stage,
  // so nothing is highlighted as the current step. The panel still points at
  // step 1, which is where such a course will begin.
  const pointerIsKnown = pointerIndex >= 0;

  const rows = SETUP_STAGES.map((stage, index) => {
    let status = "upcoming";
    if (doneFlags[index]) {
      status = "done";
    } else if (pointerIsKnown && index === effectiveIndex) {
      status = "current";
    }
    return { stage, status };
  });

  return { stepIndex: effectiveIndex + 1, effectiveStage, rows };
}
